import { noop } from '@utils/noop'
import {
  ChangeEvent,
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  useState,
} from 'react'
import { Input } from '../input'
import { convertTo12Hour } from '../util'
import { parse } from './parse'
import { Tooltip } from '@lib/tooltip'

const invalidParseResult: ParseResult = {
  hour: NaN,
  minute: NaN,
}

interface ParseResult {
  hour: number
  minute: number
}

const parseInput = (
  input: string,
  { assumePm, context }: { assumePm: boolean; context: 'time' | 'duration' },
): ParseResult => {
  const { hour, minute } = parse(input, { assumePm, context })

  if (isNaN(hour) || isNaN(minute)) return { hour, minute }
  if (hour < 0) return invalidParseResult
  if (hour === 24 && minute !== 0) return invalidParseResult
  if (hour > 24) return invalidParseResult
  if (minute < 0) return invalidParseResult
  if (minute >= 60) return invalidParseResult

  return { hour, minute }
}

const pad = (number: number) => {
  if (number >= 10) return `${number}`

  return `0${number}`
}

const useFormat =
  (mode: '12' | '24') =>
  (
    hour: number | undefined | null = null,
    minute: number | undefined | null = null,
  ): string => {
    if (hour === null) return ''
    if (minute === null) return ''

    if (mode === '24') return `${hour}:${pad(minute)}`

    const twelveHour = convertTo12Hour(hour)
    const period = hour >= 12 ? 'PM' : 'AM'
    return `${twelveHour}:${pad(minute)} ${period}`
  }

interface Props
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'onChange' | 'value'
  > {
  assumePm?: boolean

  // 12       — 12-hour clock (with AM/PM)
  // 24       — 24-hour clock (no AM/PM)
  // duration — duration in hours and minutes (similar to 24-hour clock, but parses input differently)
  displayMode?: '12' | '24' | 'duration'
  hour: number | null // 0 - 24
  minute: number | null // 0 - 60
  onChange: (hour: number | null, minute: number | null) => void // NaN when invalid
  nextDayTooltip?: string
  nextDay: boolean
}

export const TimeInput: FC<Props> = ({
  assumePm = false,
  displayMode = '12',
  hour,
  minute,
  onChange,
  onBlur = noop,
  onFocus = noop,
  nextDay,
  nextDayTooltip,
  ...props
}) => {
  const format = useFormat(displayMode === 'duration' ? '24' : displayMode)
  const [rawInput, setRawInput] = useState(format(hour, minute))
  const [isActive, setIsActive] = useState(false)

  const getDisplayValue = () => {
    if (isActive) return rawInput
    if (hour === null) return ''
    if (minute === null) return ''
    if (isNaN(hour)) return rawInput
    if (isNaN(minute)) return rawInput

    return format(hour, minute)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value
    setRawInput(raw)

    if (raw.trim() === '') return onChange(null, null)

    const parseResult = parseInput(raw, {
      assumePm,
      context: displayMode === 'duration' ? 'duration' : 'time',
    })
    onChange(parseResult.hour, parseResult.minute)
  }

  const handleBlur: Props['onBlur'] = (event) => {
    setIsActive(false)
    onBlur(event)

    if (hour === null) return setRawInput('')
    if (minute === null) return setRawInput('')
    if (!isNaN(hour) && !isNaN(minute)) setRawInput(format(hour, minute))
  }

  const handleFocus: Props['onFocus'] = (event) => {
    setIsActive(true)
    onFocus(event)

    if (hour === null) return setRawInput('')
    if (minute === null) return setRawInput('')
    if (!isNaN(hour) && !isNaN(minute)) setRawInput(format(hour, minute))
  }

  return (
    <div className="flex items-center border border-neutral-300 rounded-md">
      <Input
        {...props}
        onChange={handleChange}
        value={getDisplayValue()}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className="grow p-2 outline-none border-none"
      />
      {nextDay && (
        <>
          <span
            className="py-2 pr-2 text-neutral-400 text-xs font-semibold"
            data-tooltip-id="next-day-tooltip"
            data-tooltip-html={nextDayTooltip}
            data-tooltip-delay-show={500}
          >
            +1
          </span>
          {nextDayTooltip !== undefined && <Tooltip id="next-day-tooltip" />}
        </>
      )}
    </div>
  )
}
